import { Suspense, useEffect, useState } from 'react';
import { currentStaff, staffSignedInHere, type SignedIn } from './api.ts';
import type { ComposedLayer } from './routes.tsx';
import { usePlace } from './router.ts';

/**
 * Every feature's layer over the page in front (bindPages' `layer`), for a
 * signed-in staff member it shows to. Asked again when the path changes, so
 * signing in or out on one tab is picked up on the next page; a layer that
 * still shows stays mounted across pages, so what it records carries on.
 * A page asks only when staff signed in on this browser (sign-in sets the
 * note, and a staff page's own session read sets it too, so a browser signed
 * in before this note existed picks it up on its next page), so a customer's
 * phone never asks for a staff session.
 */
export function Layers({ layers }: { readonly layers: readonly ComposedLayer[] }) {
  const { path } = usePlace();
  const [shown, setShown] = useState<{ readonly staff: SignedIn; readonly layers: readonly ComposedLayer[] } | null>(null);

  useEffect(() => {
    if (layers.length === 0 || !staffSignedInHere()) {
      setShown(null);
      return;
    }
    const run = { live: true };
    void (async () => {
      const staff = await currentStaff().catch(() => null);
      const answers = staff === null ? [] : await Promise.all(layers.map((layer) => layer.shows(staff).catch(() => false)));
      const showing = staff === null ? [] : layers.filter((_layer, at) => answers[at] === true);
      if (!run.live) return;
      setShown((before) => {
        if (staff === null || showing.length === 0) return null;
        const same = before !== null && before.staff.staffId === staff.staffId && before.layers.length === showing.length && before.layers.every((layer, at) => layer === showing[at]);
        return same ? before : { staff, layers: showing };
      });
    })();
    return () => {
      run.live = false;
    };
  }, [layers, path]);

  if (shown === null) return null;
  return (
    <Suspense fallback={null}>
      {shown.layers.map(({ Layer }, at) => (
        <Layer key={at} staff={shown.staff} />
      ))}
    </Suspense>
  );
}
